import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Warning, Phone, Buildings, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Emergency() {
  const { user } = useAuth();
  const [hospitals, setHospitals] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  useEffect(() => { api.get("/emergency/nearby-hospitals").then((r) => setHospitals(r.data)); }, []);

  const trigger = async () => {
    if (!window.confirm("Trigger emergency SOS? Your emergency contacts will be notified.")) return;
    setTriggering(true);
    try {
      const pos = await new Promise((res) => {
        if (!navigator.geolocation) res({ coords: {} });
        else navigator.geolocation.getCurrentPosition((p) => res(p), () => res({ coords: {} }), { timeout: 3000 });
      });
      const { data } = await api.post("/emergency/sos", {
        lat: pos.coords.latitude, lng: pos.coords.longitude, note: "User-triggered SOS",
      });
      setLastEvent(data);
      toast.success("SOS triggered. Contacts and support notified.");
    } catch { toast.error("Could not trigger SOS"); }
    finally { setTriggering(false); }
  };

  const contacts = user?.profile?.emergency_contacts || [];

  return (
    <div className="p-6 md:p-12 pb-24 md:pb-12 max-w-6xl">
      <div className="mb-8">
        <div className="overline text-terracotta">Never delay emergency care</div>
        <h1 className="font-heritage text-5xl text-ayurveda mt-1">Emergency & safety</h1>
      </div>

      {/* SOS card */}
      <Card className="bg-terracotta text-white border-none mb-8 overflow-hidden" data-testid="sos-card">
        <CardContent className="p-8 md:p-12 relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
            <div>
              <Warning size={40} weight="duotone" className="mb-3" />
              <h2 className="font-heritage text-4xl mb-2">Trigger SOS</h2>
              <p className="text-white/80 max-w-md">This will alert your emergency contacts with your location. In life-threatening cases, always call 112 or 108 directly.</p>
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={trigger} disabled={triggering} className="bg-white text-terracotta hover:bg-white/90 h-14 px-8 text-base font-bold" data-testid="sos-trigger-btn">
                {triggering ? "Triggering…" : "🚨 Trigger SOS"}
              </Button>
              <a href="tel:112">
                <Button variant="outline" className="w-full h-11 border-white text-white hover:bg-white/10" data-testid="call-112">
                  <Phone size={18} className="mr-2" /> Call 112
                </Button>
              </a>
              <a href="tel:108">
                <Button variant="outline" className="w-full h-11 border-white text-white hover:bg-white/10" data-testid="call-108">
                  <Phone size={18} className="mr-2" /> Call 108
                </Button>
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {lastEvent && (
        <div className="rounded-xl bg-green-800 text-white p-4 mb-8" data-testid="sos-last-event">
          <div className="overline text-xs mb-1 text-green-200">Active SOS</div>
          <div className="text-sm">Event ID: {lastEvent.id.slice(0,8)}… · Contacts notified: {lastEvent.contacts_notified.length}</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        {/* Contacts */}
        <div>
          <div className="overline text-charcoal/50 mb-3">Your emergency contacts</div>
          {contacts.length === 0 && (
            <div className="rounded-2xl bg-white p-6 border border-black/5 text-sm text-charcoal/60">
              No contacts saved. Add contacts in your Profile.
            </div>
          )}
          <div className="space-y-2">
            {contacts.map((c, i) => (
              <Card key={i} className="border-black/5 bg-white">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-clinical text-ayurveda">{c.name || "Unnamed"}</div>
                    <div className="text-xs text-charcoal/60">{c.relation || "—"} · {c.phone}</div>
                  </div>
                  <a href={`tel:${c.phone}`}>
                    <Button size="sm" variant="outline"><Phone size={14} className="mr-1" /> Call</Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Nearby hospitals */}
        <div>
          <div className="overline text-charcoal/50 mb-3">Nearby verified hospitals</div>
          <div className="space-y-2">
            {hospitals.map((h) => (
              <Card key={h.id} className="border-black/5 bg-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Buildings size={16} weight="duotone" className="text-ayurveda" />
                        <div className="font-clinical text-ayurveda">{h.name}</div>
                      </div>
                      <div className="text-xs text-charcoal/60 flex items-center gap-1"><MapPin size={12} /> {h.city}, {h.state}</div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {h.emergency_capable && <Badge className="bg-terracotta text-white text-[10px]">Emergency</Badge>}
                        {h.specialties?.slice(0,2).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                      </div>
                    </div>
                    <a href={`tel:${h.phone}`}>
                      <Button size="sm" className="bg-ayurveda text-bone"><Phone size={14} /></Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 rounded-2xl bg-white p-6 border border-black/5">
        <div className="overline text-terracotta mb-2">Good Samaritan guidance</div>
        <p className="text-sm text-charcoal/70 leading-relaxed">
          If you help someone in an accident, you are protected under Indian law. Call 112 or 108. Stay with the person until help arrives. Do not move them unless there is immediate danger (fire, traffic).
        </p>
      </div>
    </div>
  );
}
